import { useState } from "react";
import {
  CreditCard,
  Zap,
  Check,
  Crown,
  Shield,
  Users,
  FolderKanban,
  BarChart3,
  ArrowRight,
  Loader2,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { useSubscription, type SubscriptionTier } from "../hooks/useSubscription";
import { useAuth } from "../hooks/useAuth";

// ─── Feature Labels ──────────────────────────────────────────
const FEATURE_LABELS: Record<string, { label: string; icon: typeof Check }> = {
  claims: { label: "Claims Management", icon: FolderKanban },
  vault: { label: "Document Vault", icon: Shield },
  pnl: { label: "P&L Analytics", icon: BarChart3 },
  newsletter: { label: "AI Newsletter", icon: Sparkles },
  dashpivot: { label: "Dashpivot Integration", icon: TrendingUp },
  sso: { label: "SSO / SAML", icon: Shield },
  api: { label: "API Access", icon: Zap },
};

const AI_FEATURE_LABELS: Record<string, string> = {
  chat: "AI Chat",
  report_generation: "Report Gen",
  variation_analysis: "Variation Analysis",
  pnl_insights: "P&L Insights",
  newsletter: "Newsletter",
  description_gen: "Description Gen",
  other: "Other",
};

// ─── Component ───────────────────────────────────────────────
export function BillingPage() {
  const { isPlatformAdmin } = useAuth();
  const {
    subscription,
    tier,
    tiers,
    creditsUsage,
    loading,
    isActive,
    isPastDue,
    isUnlimited,
    creditsUsed,
    creditsLimit,
    creditsRemaining,
    creditsPercent,
    isOverLimit,
    daysUntilRenewal,
  } = useSubscription();

  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "60vh",
      }}>
        <Loader2
          size={32}
          style={{ animation: "spin 1s linear infinite", color: "var(--color-primary)" }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "var(--spacing-xl)", maxWidth: 1200, margin: "0 auto" }}>
      {/* ── Header ── */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "var(--spacing-xl)",
        flexWrap: "wrap",
        gap: "var(--spacing-md)",
      }}>
        <div>
          <h1 style={{
            fontSize: "var(--font-size-2xl)",
            fontWeight: 700,
            color: "var(--text-primary)",
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-sm)",
          }}>
            <CreditCard size={28} />
            Billing & Subscription
          </h1>
          <p style={{
            color: "var(--text-muted)",
            fontSize: "var(--font-size-sm)",
            marginTop: "var(--spacing-xs)",
          }}>
            Manage your subscription plan, AI credits, and billing details.
          </p>
        </div>
      </div>

      {/* ── Past Due Warning ── */}
      {isPastDue && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
          padding: "var(--spacing-md)",
          background: "rgba(239,68,68,0.08)",
          border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: "var(--border-radius-md)",
          marginBottom: "var(--spacing-lg)",
          color: "#ef4444",
          fontSize: "var(--font-size-sm)",
        }}>
          <AlertTriangle size={18} />
          <span style={{ fontWeight: 600 }}>Payment past due.</span>
          <span style={{ color: "var(--text-muted)" }}>
            Please update your payment method to avoid service interruption.
          </span>
        </div>
      )}

      {/* ── Current Plan & Credits Grid ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: "var(--spacing-lg)",
        marginBottom: "var(--spacing-2xl)",
      }}>
        {/* Current Plan Card */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--border-radius-lg)",
          padding: "var(--spacing-xl)",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--spacing-lg)",
          }}>
            <h3 style={{
              fontSize: "var(--font-size-md)",
              fontWeight: 600,
              color: "var(--text-primary)",
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
            }}>
              <Crown size={18} style={{ color: "#f59e0b" }} />
              Current Plan
            </h3>
            <span style={{
              fontSize: "var(--font-size-xs)",
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: 99,
              background: isActive
                ? "rgba(16,185,129,0.1)"
                : "rgba(239,68,68,0.1)",
              color: isActive ? "#10b981" : "#ef4444",
            }}>
              {subscription?.status?.toUpperCase() || "NO PLAN"}
            </span>
          </div>

          <div style={{ marginBottom: "var(--spacing-lg)" }}>
            <p style={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: 700,
              color: "var(--text-primary)",
              lineHeight: 1.2,
            }}>
              {tier?.display_name || "Free"}
            </p>
            {tier && (
              <p style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--text-muted)",
                marginTop: "var(--spacing-xs)",
              }}>
                ${tier.price_monthly}<span style={{ fontSize: "var(--font-size-xs)" }}>/month</span>
                {tier.price_yearly && (
                  <span style={{ marginLeft: 8, color: "#10b981", fontWeight: 500 }}>
                    Save ${(tier.price_monthly * 12 - tier.price_yearly).toFixed(0)}/yr
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Plan Limits */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--spacing-sm)",
          }}>
            <LimitItem
              icon={<FolderKanban size={14} />}
              label="Projects"
              value={tier?.max_projects === -1 ? "Unlimited" : String(tier?.max_projects || 0)}
            />
            <LimitItem
              icon={<Users size={14} />}
              label="Members"
              value={tier?.max_members === -1 ? "Unlimited" : String(tier?.max_members || 0)}
            />
            <LimitItem
              icon={<Zap size={14} />}
              label="AI Credits"
              value={isUnlimited ? "Unlimited" : (tier?.ai_credits_monthly?.toLocaleString() || "0")}
              suffix={isUnlimited ? undefined : "/mo"}
            />
            <LimitItem
              icon={<Calendar size={14} />}
              label="Renews In"
              value={`${daysUntilRenewal}`}
              suffix=" days"
            />
          </div>

          {/* Features */}
          {tier && (
            <div style={{ marginTop: "var(--spacing-lg)", borderTop: "1px solid var(--border-color)", paddingTop: "var(--spacing-md)" }}>
              <p style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--text-muted)", marginBottom: "var(--spacing-sm)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Included Features
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-xs)" }}>
                {tier.features.map((f: string) => (
                  <span key={f} style={{
                    fontSize: "var(--font-size-xs)",
                    padding: "2px 8px",
                    borderRadius: 99,
                    background: "rgba(59,130,246,0.08)",
                    color: "var(--color-primary)",
                    fontWeight: 500,
                  }}>
                    {FEATURE_LABELS[f]?.label || f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* AI Credits Card */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--border-radius-lg)",
          padding: "var(--spacing-xl)",
        }}>
          <h3 style={{
            fontSize: "var(--font-size-md)",
            fontWeight: 600,
            color: "var(--text-primary)",
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-xs)",
            marginBottom: "var(--spacing-lg)",
          }}>
            <Zap size={18} style={{ color: "#f59e0b" }} />
            AI Credits This Month
          </h3>

          {/* Credits Progress Bar */}
          <div style={{ marginBottom: "var(--spacing-lg)" }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: "var(--spacing-xs)",
            }}>
              <span data-seq-private="" style={{
                fontSize: "var(--font-size-3xl)",
                fontWeight: 700,
                color: isOverLimit ? "#ef4444" : "var(--text-primary)",
              }}>
                {creditsUsed.toLocaleString()}
              </span>
              <span style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--text-muted)",
              }}>
                / {isUnlimited ? "Unlimited" : `${creditsLimit.toLocaleString()} credits`}
              </span>
            </div>
            {!isUnlimited && (
              <div style={{
                height: 8,
                borderRadius: 99,
                background: "var(--bg-secondary)",
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(creditsPercent, 100)}%`,
                  borderRadius: 99,
                  background: isOverLimit
                    ? "#ef4444"
                    : creditsPercent > 80
                    ? "#f59e0b"
                    : "var(--color-primary)",
                  transition: "width 0.5s ease",
                }} />
              </div>
            )}
            <p style={{
              fontSize: "var(--font-size-xs)",
              color: isOverLimit ? "#ef4444" : "var(--text-muted)",
              marginTop: "var(--spacing-xs)",
              fontWeight: isOverLimit ? 600 : 400,
            }}>
              {isUnlimited
                ? "✨ Unlimited credits, no restrictions"
                : isOverLimit
                ? "⚠️ Credit limit exceeded. AI features may be restricted."
                : `${creditsRemaining.toLocaleString()} credits remaining`}
            </p>
          </div>

          {/* Usage by Feature */}
          {creditsUsage && Object.keys(creditsUsage.credits_by_feature).length > 0 && (
            <div>
              <p style={{
                fontSize: "var(--font-size-xs)",
                fontWeight: 600,
                color: "var(--text-muted)",
                marginBottom: "var(--spacing-sm)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}>
                Usage by Feature
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                {Object.entries(creditsUsage.credits_by_feature)
                  .sort(([, a], [, b]) => b - a)
                  .map(([feature, credits]) => (
                    <div key={feature} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: "var(--border-radius-sm)",
                      background: "var(--bg-secondary)",
                    }}>
                      <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-primary)" }}>
                        {AI_FEATURE_LABELS[feature] || feature}
                      </span>
                      <span style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--text-muted)" }}>
                        {credits} credits
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {(!creditsUsage || Object.keys(creditsUsage.credits_by_feature).length === 0) && (
            <div style={{
              textAlign: "center",
              padding: "var(--spacing-xl)",
              color: "var(--text-muted)",
              fontSize: "var(--font-size-sm)",
            }}>
              <Sparkles size={32} style={{ opacity: 0.3, marginBottom: "var(--spacing-sm)" }} />
              <p>No AI credits used this billing period.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Pricing Tiers ── */}
      {isPlatformAdmin && tiers.length > 0 && (
        <>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--spacing-lg)",
          }}>
            <h2 style={{
              fontSize: "var(--font-size-xl)",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}>
              Available Plans
            </h2>

            {/* Billing Toggle */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
              background: "var(--bg-secondary)",
              borderRadius: 99,
              padding: 3,
            }}>
              <button
                onClick={() => setBillingCycle("monthly")}
                style={{
                  padding: "6px 16px",
                  borderRadius: 99,
                  border: "none",
                  fontSize: "var(--font-size-xs)",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: billingCycle === "monthly" ? "var(--bg-card)" : "transparent",
                  color: billingCycle === "monthly" ? "var(--text-primary)" : "var(--text-muted)",
                  boxShadow: billingCycle === "monthly" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                style={{
                  padding: "6px 16px",
                  borderRadius: 99,
                  border: "none",
                  fontSize: "var(--font-size-xs)",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: billingCycle === "yearly" ? "var(--bg-card)" : "transparent",
                  color: billingCycle === "yearly" ? "var(--text-primary)" : "var(--text-muted)",
                  boxShadow: billingCycle === "yearly" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                Yearly
                <span style={{
                  marginLeft: 6,
                  fontSize: "0.625rem",
                  color: "#10b981",
                  fontWeight: 700,
                }}>
                  SAVE
                </span>
              </button>
            </div>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(tiers.length, 3)}, 1fr)`,
            gap: "var(--spacing-lg)",
          }}>
            {tiers.map((t) => (
              <PricingCard
                key={t.id}
                tier={t}
                billingCycle={billingCycle}
                isCurrentPlan={tier?.id === t.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────

function LimitItem({
  icon,
  label,
  value,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
}) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--spacing-xs)",
      padding: "var(--spacing-sm)",
      background: "var(--bg-secondary)",
      borderRadius: "var(--border-radius-sm)",
    }}>
      <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
          {label}
        </p>
        <p style={{ fontSize: "var(--font-size-sm)", fontWeight: 600, color: "var(--text-primary)" }}>
          {value}{suffix || ""}
        </p>
      </div>
    </div>
  );
}

function PricingCard({
  tier,
  billingCycle,
  isCurrentPlan,
}: {
  tier: SubscriptionTier;
  billingCycle: "monthly" | "yearly";
  isCurrentPlan: boolean;
}) {
  const price =
    billingCycle === "yearly" && tier.price_yearly !== null
      ? (tier.price_yearly / 12).toFixed(0)
      : tier.price_monthly.toFixed(0);

  const isPopular = tier.name === "professional";
  const isEnterprise = tier.name === "enterprise";

  return (
    <div
      style={{
        position: "relative",
        background: "var(--bg-card)",
        border: `2px solid ${isCurrentPlan ? "var(--color-primary)" : isPopular ? "rgba(59,130,246,0.3)" : "var(--border-color)"}`,
        borderRadius: "var(--border-radius-lg)",
        padding: "var(--spacing-xl)",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        if (!isCurrentPlan) e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Popular / Current Badge */}
      {(isPopular || isCurrentPlan) && (
        <span style={{
          position: "absolute",
          top: -12,
          right: 16,
          fontSize: "var(--font-size-xs)",
          fontWeight: 700,
          padding: "3px 12px",
          borderRadius: 99,
          background: isCurrentPlan ? "var(--color-primary)" : "#3b82f6",
          color: "#fff",
        }}>
          {isCurrentPlan ? "CURRENT" : "POPULAR"}
        </span>
      )}

      <h3 style={{
        fontSize: "var(--font-size-lg)",
        fontWeight: 700,
        color: "var(--text-primary)",
        marginBottom: "var(--spacing-sm)",
      }}>
        {tier.display_name}
      </h3>

      <div style={{ marginBottom: "var(--spacing-lg)" }}>
        <span style={{
          fontSize: "var(--font-size-3xl)",
          fontWeight: 700,
          color: "var(--text-primary)",
        }}>
          ${price}
        </span>
        <span style={{
          fontSize: "var(--font-size-sm)",
          color: "var(--text-muted)",
        }}>
          /month
        </span>
        {billingCycle === "yearly" && tier.price_yearly && (
          <p style={{
            fontSize: "var(--font-size-xs)",
            color: "#10b981",
            fontWeight: 500,
            marginTop: 2,
          }}>
            Billed ${tier.price_yearly}/year
          </p>
        )}
      </div>

      {/* Limits */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-xs)",
        marginBottom: "var(--spacing-lg)",
        flex: 1,
      }}>
        <PlanLimit
          label="Projects"
          value={tier.max_projects === -1 ? "Unlimited" : `Up to ${tier.max_projects}`}
        />
        <PlanLimit
          label="Team Members"
          value={tier.max_members === -1 ? "Unlimited" : `Up to ${tier.max_members}`}
        />
        <PlanLimit
          label="AI Credits"
          value={tier.ai_credits_monthly === -1 ? "Unlimited" : `${tier.ai_credits_monthly.toLocaleString()}/month`}
        />

        {/* Features */}
        {tier.features.map((f: string) => (
          <div key={f} style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-xs)",
            fontSize: "var(--font-size-xs)",
            color: "var(--text-primary)",
          }}>
            <Check size={14} style={{ color: "#10b981", flexShrink: 0 }} />
            {FEATURE_LABELS[f]?.label || f}
          </div>
        ))}

        {isEnterprise && (
          <>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
              fontSize: "var(--font-size-xs)",
              color: "var(--text-primary)",
            }}>
              <Check size={14} style={{ color: "#10b981", flexShrink: 0 }} />
              Priority Support
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
              fontSize: "var(--font-size-xs)",
              color: "var(--text-primary)",
            }}>
              <Check size={14} style={{ color: "#10b981", flexShrink: 0 }} />
              Custom Branding
            </div>
          </>
        )}
      </div>

      {/* CTA Button */}
      <button
        disabled={isCurrentPlan}
        style={{
          width: "100%",
          padding: "10px 16px",
          borderRadius: "var(--border-radius-md)",
          border: isCurrentPlan ? "1px solid var(--border-color)" : "none",
          background: isCurrentPlan
            ? "transparent"
            : isPopular
            ? "var(--color-primary)"
            : "var(--bg-secondary)",
          color: isCurrentPlan
            ? "var(--text-muted)"
            : isPopular
            ? "#fff"
            : "var(--text-primary)",
          fontSize: "var(--font-size-sm)",
          fontWeight: 600,
          cursor: isCurrentPlan ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--spacing-xs)",
          transition: "background 0.2s ease, transform 0.1s ease",
        }}
        onMouseEnter={(e) => {
          if (!isCurrentPlan) e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {isCurrentPlan ? (
          "Current Plan"
        ) : (
          <>
            Upgrade <ArrowRight size={14} />
          </>
        )}
      </button>
    </div>
  );
}

function PlanLimit({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px solid var(--border-color)",
      fontSize: "var(--font-size-xs)",
    }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{value}</span>
    </div>
  );
}
